import Reflux from "reflux";
import URI from "urijs";
import { CloudForwarderForwarders } from "@graylog/enterprise-api";
import fetch, { fetchPeriodically } from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
export const ForwardersActions = Reflux.createActions({
  list: { asyncResult: true },
  listAll: { asyncResult: true },
  listPendingConfiguration: { asyncResult: true },
  get: { asyncResult: true },
  update: { asyncResult: true },
  delete: { asyncResult: true },
  removeConfiguration: { asyncResult: true },
  updates: { asyncResult: true }
});
const ForwardersStore = Reflux.createStore({
  listenables: [ForwardersActions],
  sourceUrl: "/plugins/org.graylog.plugins.forwarder/forwarder/forwarders",
  all: void 0,
  forwardersPendingConfiguration: [],
  forwardersPendingConfigurationPagination: {
    total: 0,
    count: 0,
    page: 1,
    perPage: 10,
    query: ""
  },
  getInitialState() {
    return this.getState();
  },
  getState() {
    return {
      all: this.all,
      forwardersPendingConfiguration: this.forwardersPendingConfiguration,
      forwardersPendingConfigurationPagination: this.forwardersPendingConfigurationPagination
    };
  },
  propagateUpdate() {
    this.trigger(this.getState());
  },
  _forwardersUrl({ segments = [], query = {} }) {
    const uri = new URI(this.sourceUrl);
    const nextSegments = uri.segment().concat(segments);
    uri.segmentCoded(nextSegments);
    uri.query(query);
    return qualifyUrl(uri.resource());
  },
  listPendingConfiguration({ query = "", page = 1, pageSize = 0, order = "desc", sortByField = "created_at" }, isPeriodicalRequest = false) {
    const fetchMethod = isPeriodicalRequest ? fetchPeriodically : fetch;
    const promise = fetchMethod(
      "GET",
      this._forwardersUrl({
        segments: ["pending_configuration"],
        query: {
          query,
          page,
          per_page: pageSize,
          order,
          sort: sortByField
        }
      })
    );
    promise.then(
      (response) => {
        this.forwardersPendingConfiguration = response.forwarders;
        this.forwardersPendingConfigurationPagination = {
          total: response.total,
          count: response.count,
          page: response.page,
          perPage: response.per_page,
          query
        };
        this.propagateUpdate();
        return response;
      },
      (error) => {
        UserNotification.error(`Retrieving List of Forwarders that are pending configuration failed with error : ${error},
        Could not get Forwarder list.`);
      }
    );
    ForwardersActions.listPendingConfiguration.promise(promise);
  },
  list({
    query = "",
    page = 1,
    pageSize = 10,
    order = "asc",
    sortByField = "state",
    stateFilter = "any"
  }, isPeriodicalRequest = false) {
    const fetchMethod = isPeriodicalRequest ? fetchPeriodically : fetch;
    const promise = fetchMethod(
      "GET",
      this._forwardersUrl({
        query: {
          query,
          page,
          per_page: pageSize,
          order,
          sort: sortByField,
          state: stateFilter
        }
      })
    ).then(
      (response) => ({
        forwarders: response.forwarders,
        forwardersPagination: {
          total: response.total,
          count: response.count,
          page: response.page,
          perPage: response.per_page,
          query,
          order,
          sortByField,
          stateFilter
        },
        forwardersContext: response.context.forwarder
      })
    );
    promise.catch((error) => {
      UserNotification.error(`Retrieving List of Forwarders failed with error : ${error},
          Could not get Forwarder list.`);
    });
    ForwardersActions.list.promise(promise);
  },
  listAll() {
    const promise = fetch(
      "GET",
      this._forwardersUrl({
        query: {
          query: "",
          page: 1,
          per_page: 0
        }
      })
    );
    promise.then(
      (response) => {
        this.all = response.forwarders;
        this.propagateUpdate();
        return response;
      },
      (error) => {
        UserNotification.error(`Retrieving List of Forwarders failed with error : ${error},
        Could not get Forwarder list.`);
      }
    );
    ForwardersActions.listAll.promise(promise);
  },
  get(id) {
    const url = qualifyUrl(`${this.sourceUrl}/${id}`);
    const promise = fetchPeriodically("GET", url).then(
      (response) => ({
        forwarder: response.forwarder,
        forwarderContext: response.context.forwarder
      })
    );
    promise.catch((error) => {
      UserNotification.error(`Fetching forwarder failed with status: ${error.message},
        Could not retrieve forwarder with id:${id}`);
    });
    ForwardersActions.get.promise(promise);
  },
  update(forwarderId, updatedForwarder) {
    const update = {
      title: updatedForwarder.title,
      description: updatedForwarder.description,
      hostname: updatedForwarder.hostname,
      input_profile_id: updatedForwarder.input_profile_id
    };
    const promise = CloudForwarderForwarders.update(encodeURIComponent(forwarderId), update);
    promise.then(
      (response) => {
        UserNotification.success("Forwarder details updated successfully");
        this.get(forwarderId);
        this.list({});
        return response;
      },
      (error) => {
        UserNotification.error(`Forwarder update failed with status: ${error.message},
        Could not update forwarder ${updatedForwarder.title}`);
      }
    );
    ForwardersActions.update.promise(promise);
  },
  updates(updatedForwarders) {
    const promises = updatedForwarders.map(
      (uf) => fetch("PUT", qualifyUrl(`${this.sourceUrl}/${uf.id}`), {
        title: uf.title,
        description: uf.description,
        input_profile_id: uf.input_profile_id
      })
    );
    Promise.all(promises).then(() => {
      this.listAll();
      UserNotification.success("Updated all forwarders successfully");
    }).catch((error) => {
      UserNotification.error(`Updating forwarders failed with status: ${error.message},
       Could not update forwarders`);
    });
  },
  delete(forwarderId) {
    const promise = fetch("DELETE", this._forwardersUrl({ segments: [forwarderId] }));
    promise.then(
      (response) => {
        UserNotification.success("Forwarder successfully deleted.");
        this.list({});
        return response;
      },
      (error) => {
        UserNotification.error(`Deleting Forwarder failed with status: ${error.message},
        Could not delete forwarder with id: ${forwarderId}`);
      }
    );
    ForwardersActions.delete.promise(promise);
  },
  removeConfiguration(forwarder) {
    const { title, description, hostname } = forwarder;
    const update = {
      title,
      description,
      hostname
    };
    const promise = fetch("PUT", this._forwardersUrl({ segments: [forwarder.id] }), update);
    promise.then(
      (response) => {
        UserNotification.success("Forwarder configuration successfully removed.");
        this.list({});
        return response;
      },
      (error) => {
        UserNotification.error(`Removing Forwarder configuration failed with status: ${error.message},
      Could not remove forwarder configuration with id: ${forwarder.id}`);
      }
    );
    ForwardersActions.removeConfiguration.promise(promise);
  }
});
export default ForwardersStore;
